import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CommunityCard } from '../components/Communities/CommunityCard';
import { SkeletonGrid } from '../components/ui/SkeletonCard';
import { CreateCommunityModal } from '../components/Communities/CreateCommunityModal';
import { SubscriptionModal } from '../components/Subscription/SubscriptionModal';
import { Meta } from '../components/Meta';
import { supabase } from '../lib/supabase';
import { isProfessional } from '../lib/auth/authService';
import { Leaf } from 'lucide-react';
import { CommunityFilters } from '../components/Communities/Filters/CommunityFilters';
import { getCurrentLocation, filterByDistance, type LocationCoords } from '../lib/utils/locationFilter';
import type { Community } from '../types/communities';


interface Filters {
  type: string[];
  nearMe: boolean;
}

export function Communities() {
  const { t } = useTranslation();
  const [communities, setCommunities] = useState<Community[]>([]);
  const [filteredCommunities, setFilteredCommunities] = useState<(Community & { distance?: number })[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [canCreateCommunity, setCanCreateCommunity] = useState(false);
  const [userLocation, setUserLocation] = useState<LocationCoords | null>(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [filters, setFilters] = useState<Filters>({
    type: ['all'],
    nearMe: false
  });
  const [searchQuery, setSearchQuery] = useState('');

  const fetchCommunities = async () => {
    try {
      // First fetch all communities
      const { data: communitiesData, error: communitiesError } = await supabase
        .from('communities')
        .select(`
          *,
          owner:users!communities_owner_id_fkey (
            id,
            username,
            avatar_url
          )
        `);

      if (communitiesError) throw communitiesError;

      // Then fetch member counts and sample avatars separately to avoid recursion
      const communitiesWithCounts = await Promise.all(
        communitiesData.map(async (community) => {
          // Get member count
          const { count } = await supabase
            .from('community_members')
            .select('*', { count: 'exact', head: true })
            .eq('community_id', community.id);

          // Get sample member avatars (first 5)
          const { data: membersData } = await supabase
            .from('community_members')
            .select(`
              user_id,
              user:users!community_members_user_id_fkey (
                id,
                avatar_url,
                username
              )
            `)
            .eq('community_id', community.id)
            .limit(5);

          const memberAvatars = membersData?.map(m => ({
            user_id: m.user_id,
            avatar_url: m.user?.avatar_url,
            username: m.user?.username
          })) || [];

          return {
            ...community,
            _count: {
              members: count || 0
            },
            memberAvatars
          };
        })
      );

      setCommunities(communitiesWithCounts);
    } catch (err: any) {
      console.error('Error fetching communities:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check authentication status
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsAuthenticated(!!session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session);
      if (session) {
        checkAccess();
      } else {
        setCanCreateCommunity(false);
      }
    });

    // Fetch communities regardless of auth status
    fetchCommunities();

    return () => subscription.unsubscribe();
  }, []);

  // Apply filters
  useEffect(() => {
    let filtered = [...communities];

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(community =>
        community.name.toLowerCase().includes(query) ||
        community.description?.toLowerCase().includes(query)
      );
    }

    // Filter by location
    if (filters.nearMe && userLocation) {
      const nearby = filterByDistance(filtered, userLocation, 100);
      setFilteredCommunities(nearby);
    } else {
      setFilteredCommunities(filtered);
    }
  }, [communities, filters, userLocation, searchQuery]);

  const checkAccess = async () => {
    const hasProfessionalAccess = await isProfessional();
    setCanCreateCommunity(hasProfessionalAccess);
  };

  const handleCreateCommunity = () => {
    if (!isAuthenticated) {
      window.dispatchEvent(new CustomEvent('show-auth'));
    } else if (canCreateCommunity) {
      setShowCreateModal(true);
    } else {
      setShowUpgradeModal(true);
    }
  };

  const handleFilterChange = async (filterType: keyof Filters, values: string[] | boolean) => {
    // Handle Near Me filter specially
    if (filterType === 'nearMe' && values === true && !userLocation) {
      setLocationLoading(true);
      try {
        const location = await getCurrentLocation();
        setUserLocation(location);
        setFilters(prev => ({ ...prev, nearMe: true }));
      } catch (err: any) {
        setError(err.message);
        setFilters(prev => ({ ...prev, nearMe: false }));
      } finally {
        setLocationLoading(false);
      }
      return;
    }

    setFilters(prev => ({
      ...prev,
      [filterType]: values
    }));
  };

  if (loading) {
    return (
      <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10 py-8">
        <SkeletonGrid count={8} variant="community" columns="grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" />
      </div>
    );
  }

  return (
    <div>
      <Meta
        title={`${t('communities.title')} | Mindful Family`}
        description={t('communities.subtitle')}
      />

      {/* Mobile Full-Width Header */}
      <div className="lg:hidden relative h-64 overflow-hidden">
        <img
          src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/listing-images/123c446f-e80c-409d-a3d3-e6fdc14949d4/humans-connecting.png"
          alt="Find Your Wellness Community"
          className="w-full h-full object-cover shadow-none"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-gray-50"></div>
      </div>

      {/* Mobile Content Below Image */}
      <div className="lg:hidden px-4 py-8 text-center bg-gray-50">
        <h1 className="text-3xl font-gelica font-bold text-content mb-4">
          {t('communities.title')}
        </h1>
        <p className="text-lg text-content/70 mb-6 max-w-md mx-auto">
          {t('communities.subtitle')}
        </p>
        <div className="flex flex-row items-center justify-center gap-2">
          <button
            onClick={() => {
              const communitiesSection = document.querySelector('[data-communities-section]');
              if (communitiesSection) {
                communitiesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
            className="inline-flex items-center justify-center px-4 py-2 text-sm bg-accent-text text-white rounded-full font-medium hover:bg-accent-text/90 transition-colors shadow-sm whitespace-nowrap"
          >
            {t('communities.exploreCommunities')}
          </button>
          {isAuthenticated ? (
            <button
              onClick={handleCreateCommunity}
              className="inline-flex items-center justify-center px-4 py-2 text-sm border-2 border-accent-text text-accent-text rounded-full font-medium hover:bg-accent-base/10 transition-colors whitespace-nowrap"
            >
              {t('communities.createCommunity')}
            </button>
          ) : (
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('show-auth'))}
              className="inline-flex items-center justify-center px-4 py-2 text-sm border-2 border-accent-text text-accent-text rounded-full font-medium hover:bg-accent-base/10 transition-colors whitespace-nowrap"
            >
              {t('communities.createCommunity')}
            </button>
          )}
        </div>
      </div>

      {/* Desktop Hero */}
      <div className="hidden lg:block">
        <div className="relative mb-12 bg-gradient-to-b from-[#F3F7EE] to-gray-50 overflow-hidden">
          <div className="py-8 lg:py-16">
            <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                <div className="text-center lg:text-left">
                  <h1 className="text-4xl lg:text-5xl font-gelica leading-tight mb-6">
                    {t('communities.title')}
                  </h1>
                  <p className="text-lg lg:text-xl text-content/70 mb-6">
                    {t('communities.subtitle')}
                  </p>
                  <div className="flex flex-col sm:flex-row items-center lg:items-start gap-3 mb-6">
                    <button
                      onClick={() => {
                        const communitiesSection = document.querySelector('[data-communities-section]');
                        if (communitiesSection) {
                          communitiesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }}
                      className="inline-flex items-center justify-center px-6 lg:px-10 py-3 bg-accent-text text-white rounded-full font-medium hover:bg-accent-text/90 transition-colors whitespace-nowrap"
                    >
                      {t('communities.exploreCommunities')}
                    </button>
                    {isAuthenticated ? (
                      <button
                        onClick={handleCreateCommunity}
                        className="inline-flex items-center justify-center px-6 lg:px-10 py-3 border-2 border-accent-text text-accent-text rounded-full font-medium hover:bg-accent-base/10 transition-colors whitespace-nowrap"
                      >
                        {t('communities.createCommunity')}
                      </button>
                    ) : (
                      <button
                        onClick={() => window.dispatchEvent(new CustomEvent('show-auth'))}
                        className="inline-flex items-center justify-center px-6 lg:px-10 py-3 border-2 border-accent-text text-accent-text rounded-full font-medium hover:bg-accent-base/10 transition-colors whitespace-nowrap"
                      >
                        {t('communities.createCommunity')}
                      </button>
                    )}
                  </div>
                </div>
                <div className="hidden lg:block">
                  <img
                    src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/listing-images/123c446f-e80c-409d-a3d3-e6fdc14949d4/humans-connecting.png"
                    alt="Wellness Communities"
                    className="w-full h-auto blob-shape"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10 py-8" data-communities-section>
        <CommunityFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          locationLoading={locationLoading}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
        />

        {error && (
          <div className="text-red-600 mb-4">{error}</div>
        )}

        {communities.length === 0 ? (
          <div className="bg-background rounded-lg p-8 text-center">
            <p className="text-content">{t('communities.noCommunitiesAvailable')}</p>
          </div>
        ) : (
          <>
            {!isAuthenticated && (
              <div className="bg-accent-base/10 border border-accent-text/10 rounded-lg p-4 mb-6">
                <p className="text-content/80">
                  {t('communities.signInToJoin')}
                  <button
                    onClick={() => window.dispatchEvent(new CustomEvent('show-auth'))}
                    className="ml-2 text-accent-text hover:text-accent-text/80"
                  >
                    {t('communities.signInNow')}
                  </button>
                </p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-6">
              {filteredCommunities.length === 0 ? (
                <div className="col-span-full text-center py-8 text-content/60">
                  {searchQuery
                    ? `No communities found for "${searchQuery}"`
                    : filters.nearMe
                      ? t('communities.noCommunitiesNearby')
                      : t('communities.noCommunities')}
                </div>
              ) : filteredCommunities.map((community) => (
                <div
                  key={community.id}
                  onClick={() => !isAuthenticated && window.dispatchEvent(new CustomEvent('show-auth'))}
                  className={!isAuthenticated ? 'cursor-pointer' : undefined}
                >
                  <CommunityCard
                    community={community}
                    disabled={!isAuthenticated}
                    distance={(community as any).distance}
                  />
                </div>
              ))}
            </div>
          </>
        )}

        {showCreateModal && (
          <CreateCommunityModal
            onClose={() => setShowCreateModal(false)}
            onSuccess={() => {
              setShowCreateModal(false);
              fetchCommunities();
            }}
          />
        )}

        {showUpgradeModal && (
          <SubscriptionModal
            onClose={() => setShowUpgradeModal(false)}
            onSuccess={() => {
              setShowUpgradeModal(false);
              checkAccess();
            }}
          />
        )}
      </div>
    </div>
  );
}
